/**
 * useModuleAccess
 *
 * Provides helpers to check if the current user has access to specific modules
 * based on their subscription plan.
 *
 * Usage in Options API components:
 *   import { hasModuleAccess } from '@/composables/useModuleAccess';
 *   methods: { hasModuleAccess }
 *   template: v-if="hasModuleAccess('complex_projects')"
 *
 * Usage in Composition API:
 *   import { useModuleAccess } from '@/composables/useModuleAccess';
 *   const { hasModuleAccess } = useModuleAccess();
 */

/**
 * Check if the current user has access to a specific module.
 *
 * @param {string} moduleSlug - The module slug to check (e.g. 'complex_projects', 'statistics')
 * @returns {Promise<boolean>} - Promise that resolves to boolean
 */
export async function hasModuleAccess(moduleSlug) {
    try {
        const jwtToken = localStorage.getItem('userToken');
        if (!jwtToken) return false;

        const response = await fetch(`/api/admin/checkModuleAccess.php?module=${encodeURIComponent(moduleSlug)}`, {
            headers: { Authorization: `Bearer ${jwtToken}` }
        });
        const result = await response.json();
        
        if (result.success) {
            return result.has_access;
        } else {
            console.error('Module access check failed:', result.message);
            return false;
        }
    } catch (error) {
        console.error('Error checking module access:', error);
        return false;
    }
}

/**
 * Check multiple modules at once.
 *
 * @param {string[]} moduleSlugs - Array of module slugs to check
 * @returns {Promise<Object>} - Promise that resolves to object with module access status
 */
export async function checkMultipleModules(moduleSlugs) {
    const results = {};
    
    for (const slug of moduleSlugs) {
        results[slug] = await hasModuleAccess(slug);
    }
    
    return results;
}

/**
 * Check access for a known set of module slugs and return the ones the user can access.
 *
 * @param {string[]} moduleSlugs - Array of module slugs to check
 * @returns {Promise<string[]>} - Promise that resolves to array of accessible module slugs
 */
export async function getAccessibleModules(moduleSlugs) {
    try {
        const accessResults = await checkMultipleModules(moduleSlugs);
        return Object.entries(accessResults)
            .filter(([, hasAccess]) => hasAccess)
            .map(([slug]) => slug);
    } catch (error) {
        console.error('Error getting accessible modules:', error);
        return [];
    }
}

/**
 * Composition API wrapper.
 * Returns the same helpers bound to the reactive user data.
 */
export function useModuleAccess() {
    return {
        hasModuleAccess,
        checkMultipleModules,
        getAccessibleModules
    };
}
